import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { useSignedUrls } from "@/components/SignedImage";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";
import { NotificationBell } from "@/components/NotificationBell";
import { toast } from "sonner";
import { MapPin, Calendar, Ruler, Menu, User, ArrowLeft, CheckCircle2, Image as ImageIcon, MessageSquare, AlertTriangle, Package, Send, Sparkles, Mic, MicOff } from "lucide-react";
import Logo from "@/components/Logo";
import { AppSidebar } from "@/components/AppSidebar";
import { useProjects } from "@/hooks/useProjects";
import { DynamicQuoteCard } from "@/components/DynamicQuoteCard";
import { DynamicQuoteForm, type DynamicQuoteSentData } from "@/components/DynamicQuoteForm";
import { resolveSlug } from "@/config/serviceCatalog";
import { getServiceModule } from "@/services/registry";
import type { ServiceModule } from "@/services/types";
import { hasPlugin } from "@/services/pluginResolver";
import { GenericIntakeForm, type IntakeField } from "@/components/GenericIntakeForm";
import { mapQuoteToSupplierInsert } from "@/lib/quoteToSupplier";
import type { QuoteData } from "@/lib/quoteSchema";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useSupplierEntitlements } from "@/hooks/useSupplierEntitlements";
import { FEATURE_KEYS } from "@/lib/featureCatalog";
import { FeatureGate } from "@/components/FeatureGate";

// Facade-specific components: lazy-loaded, only bundled when the facade service is active
const SupplierQuoteBuilder = lazy(() => import("@/services/fasady/SupplierQuoteBuilder").then(m => ({ default: m.SupplierQuoteBuilder })));
const MaterialInquiryBuilder = lazy(() => import("@/services/fasady/MaterialInquiryBuilder").then(m => ({ default: m.MaterialInquiryBuilder })));
const MaterialInquiryResults = lazy(() => import("@/services/fasady/MaterialInquiryResults").then(m => ({ default: m.MaterialInquiryResults })));
const MaterialCostSummary = lazy(() => import("@/services/fasady/MaterialCostSummary").then(m => ({ default: m.MaterialCostSummary })));


interface WallSummary {
  area_m2: number | null;
  material: string | null;
  thickness_cm: number | null;
}

interface Supplier {
  id: string;
  company_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  region: string | null;
  premium_modules: string[];
  enabled_services: string[];
  enabled_plugins: string[];
}

interface Inquiry {
  id: string;
  title: string;
  description: string | null;
  region: string | null;
  status: string;
  photos: string[] | null;
  created_at: string;
  lat: number | null;
  lng: number | null;
  engine_id: string | null;
  intake_data: Record<string, unknown>;
}

function SimpleQuoteForm({ supplierId, inquiryId, inquiryRegion, onSubmit }: {
  supplierId: string;
  inquiryId: string;
  inquiryRegion: string | null;
  onSubmit: (totalPrice: number, notes: string) => void;
}) {
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const { t } = useI18n();

  const handleSubmit = async () => {
    const totalPrice = parseFloat(price);
    if (!totalPrice || totalPrice <= 0) { toast.error("Zadejte cenu"); return; }
    setSaving(true);
    const { error } = await supabase.from("supplier_quotes").insert({
      supplier_id: supplierId,
      inquiry_id: inquiryId,
      total_price: totalPrice,
      region: inquiryRegion || null,
      notes: notes || null,
    } as any);
    if (error) {
      setSaving(false);
      if ((error as { code?: string }).code === "23505") {
        toast.info(t("supplier.quoteAlreadySent"));
      } else {
        toast.error(error.message);
      }
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-customer-quote`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ inquiry_id: inquiryId, total_price: totalPrice }),
        });
      }
    } catch { /* best-effort */ }
    setSaving(false);
    toast.success(t("supplier.quoteSentSuccess"));
    onSubmit(totalPrice, notes);
  };

  return (
    <div className="border border-border rounded-xl bg-card p-5 space-y-4">
      <h3 className="font-semibold text-foreground text-sm flex items-center gap-1.5">
        <Send className="w-4 h-4 text-primary" />
        Odeslat cenovou nabídku
      </h3>
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Celková cena (Kč bez DPH)</label>
        <input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Např. 85000"
          className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2.5 text-foreground placeholder:text-muted-foreground/50"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Poznámky k nabídce</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Např. termín realizace, co je v ceně zahrnuto..."
          className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2.5 text-foreground resize-none placeholder:text-muted-foreground/50"
        />
      </div>
      <button
        onClick={handleSubmit}
        disabled={saving || !price}
        className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        <Send className="w-4 h-4" />
        {saving ? "Odesílám..." : "Odeslat nabídku"}
      </button>
    </div>
  );
}

export default function SupplierInquiryDetail() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const [searchParams] = useSearchParams();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { sites, chats, deleteChat, assignChat, addSite } = useProjects();

  // Central entitlement resolver for supplier premium features
  const { canUse } = useSupplierEntitlements(supplier?.id, supplier ? {
    enabled_services: supplier.enabled_services,
    enabled_plugins: supplier.enabled_plugins,
    premium_modules: supplier.premium_modules,
  } : undefined);

  const [quoteSent, setQuoteSent] = useState(false);
  const [sentQuoteData, setSentQuoteData] = useState<{
    totalPrice: number;
    materialCost: number | null;
    scaffoldingCost: number | null;
    laborCost: number | null;
    transportCost: number | null;
    otherCosts: { label: string; value: number }[] | null;
    profit: number | null;
    vatRate: number;
    area: number;
  } | null>(null);
  const [approved, setApproved] = useState(false);
  const [savingQuote, setSavingQuote] = useState(false);
  const [wallsTotalArea, setWallsTotalArea] = useState(0);
  const [wallsData, setWallsData] = useState<WallSummary[]>([]);
  const signedPhotos = useSignedUrls(inquiry?.photos || []);
  const tabParam = searchParams.get("tab");
  const [activeAction, setActiveAction] = useState<"material" | "quote">(tabParam === "material" ? "material" : "quote");
  const [quoteMode, setQuoteMode] = useState<"manual" | "generative">("manual");
  const [generativeInput, setGenerativeInput] = useState("");
  const [generatedQuote, setGeneratedQuote] = useState<QuoteData | null>(null);
  const [selectedMaterialOffer, setSelectedMaterialOffer] = useState<{ price: number; companyName: string } | null>(null);
  const [generatingQuote, setGeneratingQuote] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [quoteConfig, setQuoteConfig] = useState<{ cost_fields: { key: string; label: string; icon: string; hint?: string | null }[]; show_profit: boolean; show_vat: boolean; unit_label: string } | null>(null);
  const [serviceModule, setServiceModule] = useState<ServiceModule | null>(null);
  const [intakeSchema, setIntakeSchema] = useState<IntakeField[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { isListening, isSupported, error: speechError, start: startSpeech, stop: stopSpeech } = useSpeechRecognition({
    onResult: (transcript) => setGenerativeInput((prev) => (prev ? `${prev} ${transcript}` : transcript)),
  });

  const handleGenerateQuote = useCallback(async () => {
    if (!generativeInput.trim()) return;
    setGenerateError(null);
    setGeneratingQuote(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Nejste přihlášen");
        return;
      }
      const d = parseDescription(inquiry?.description ?? null);
      const body = {
        text: generativeInput.trim(),
        inquiry_context: {
          area_m2: wallsTotalArea > 0 ? wallsTotalArea : (parseFloat(d["Plocha"] || d["Plocha fasády"] || "0") || null),
          material: d["Materiál"] || d["Material"] || null,
          thickness: d["Tloušťka"] || d["Tloušťka izolace"] || null,
        },
      };
      let res = await supabase.functions.invoke("generate-quote", { body });
      for (let retry = 0; retry < 2 && res.error?.message?.includes("Failed to send"); retry++) {
        await new Promise((r) => setTimeout(r, 800 * (retry + 1)));
        res = await supabase.functions.invoke("generate-quote", { body });
      }
      if (res.error) throw res.error;
      const quote = (res.data as { quote?: QuoteData })?.quote;
      if (quote && typeof quote === "object" && Array.isArray(quote.dynamic_parameters)) {
        setGeneratedQuote(quote as QuoteData);
      } else {
        setGenerateError("AI vrátila neplatný formát");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Nepodařilo se zpracovat nabídku";
      setGenerateError(
        msg.includes("Failed to send")
          ? "Spojení se serverem selhalo. Zkontrolujte internet a zkuste znovu."
          : msg
      );
    } finally {
      setGeneratingQuote(false);
    }
  }, [generativeInput, wallsTotalArea, inquiry?.description]);

  const parseDescription = (desc: string | null): Record<string, string> => {
    if (!desc) return {};
    const result: Record<string, string> = {};
    for (const line of desc.split("\n")) {
      const idx = line.indexOf(":");
      if (idx > 0) {
        const key = line.slice(0, idx).trim();
        const val = line.slice(idx + 1).trim();
        if (val && val !== "null") result[key] = val;
      }
    }
    return result;
  };

  const loadData = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate("/supplier/register"); return; }

    const { data: supplierData } = await supabase
      .from("suppliers")
      .select("id, company_name, contact_email, contact_phone, region, premium_modules, enabled_services, enabled_plugins")
      .eq("user_id", session.user.id)
      .single();

    if (!supplierData) { navigate("/supplier/register"); return; }
    setSupplier(supplierData);

    if (!inquiryId) { navigate("/supplier/dashboard"); return; }

    const [inquiryRes, quoteRes, wallsRes] = await Promise.all([
      supabase.from("inquiries").select("id, title, description, region, status, photos, created_at, lat, lng, engine_id, intake_data").eq("id", inquiryId).single(),
      supabase.from("supplier_quotes").select("inquiry_id, customer_approved, total_price, material_cost, scaffolding_cost, labor_cost, transport_cost, other_costs, profit, vat_rate, area_m2").eq("supplier_id", supplierData.id).eq("inquiry_id", inquiryId),
      supabase.from("inquiry_walls").select("area_m2, material, thickness_cm").eq("inquiry_id", inquiryId),
    ]);

    if (!inquiryRes.data) { navigate("/supplier/dashboard"); return; }
    setInquiry(inquiryRes.data);

    if (wallsRes.data) {
      const walls = wallsRes.data as WallSummary[];
      setWallsData(walls);
      const total = walls.reduce((sum, w) => sum + (w.area_m2 || 0), 0);
      setWallsTotalArea(total);
    }

    if (quoteRes.data && quoteRes.data.length > 0) {
      setQuoteSent(true);
      if (quoteRes.data.some((q: any) => q.customer_approved)) setApproved(true);
      // Populate sentQuoteData from DB so confirmation screen renders on reload
      const q: any = quoteRes.data[0];
      setSentQuoteData({
        totalPrice: q.total_price || 0,
        materialCost: q.material_cost ?? null,
        scaffoldingCost: q.scaffolding_cost ?? null,
        laborCost: q.labor_cost ?? null,
        transportCost: q.transport_cost ?? null,
        otherCosts: Array.isArray(q.other_costs) ? q.other_costs : null,
        profit: q.profit ?? null,
        vatRate: q.vat_rate ?? 12,
        area: q.area_m2 ?? 0,
      });
    }

    // Load service module for engine-specific UI
    const engineId = inquiryRes.data.engine_id;
    if (engineId) {
      getServiceModule(engineId).then(setServiceModule);
    }

    // Fetch service-specific quote config (for DynamicQuoteForm) and intake_schema
    if (engineId) {
      try {
        const { data: svcData } = await supabase
          .from("services")
          .select("id, intake_schema")
          .eq("slug", resolveSlug(engineId))
          .single();
        if (svcData) {
          // Set intake_schema for generic display
          if (svcData.intake_schema && Array.isArray(svcData.intake_schema) && svcData.intake_schema.length > 0) {
            setIntakeSchema(svcData.intake_schema as IntakeField[]);
          }
          const { data: cfgData } = await supabase
            .from("service_quote_configs")
            .select("cost_fields, show_profit, show_vat, unit_label")
            .eq("service_id", svcData.id)
            .eq("status", "approved")
            .single();
          if (cfgData) setQuoteConfig(cfgData as any);
        }
      } catch { /* no config = use fallback SimpleQuoteForm */ }
    }

    setLoading(false);
  }, [inquiryId, navigate]);

  useEffect(() => { loadData(); }, [loadData]);



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">...</div>
      </div>
    );
  }

  if (!inquiry || !supplier) return null;

  const details = parseDescription(inquiry.description);
  // Plugin resolution: plugin flow only when both service + plugin are enabled for THIS supplier
  const isFasady = hasPlugin(inquiry.engine_id, supplier?.enabled_services ?? [], supplier?.enabled_plugins ?? []);
  const canUseMaterialInquiry = canUse(FEATURE_KEYS.MATERIAL_INQUIRY);

  return (
    <div className="flex h-[100dvh] bg-background overflow-hidden">
      <AppSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onClose={() => setSidebarOpen(false)}
        isLoggedIn={true}
        sites={sites}
        chats={chats}
        onNewChat={() => navigate("/")}
        onSelectChat={() => navigate("/")}
        onDeleteChat={async (id) => { await deleteChat(id); }}
        onAssignChat={async (chatId, siteId) => { await assignChat(chatId, siteId); }}
        onAddSite={async (name) => { await addSite(name); }}
      />

      <div className={`flex-1 flex flex-col h-full overflow-hidden transition-all duration-200 ${sidebarOpen ? "lg:ml-[260px]" : ""}`}>
        <header className="flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-0.5 sm:gap-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`p-1.5 sm:p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground -ml-1 sm:ml-0 ${sidebarOpen ? "lg:hidden" : ""}`}
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" className="flex items-center gap-1 text-foreground text-base hover:opacity-70 transition-opacity">
              <Logo size={32} />
              <span className="font-semibold">cenik.ai</span>
            </Link>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/profile" className="relative p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground">
              <User className="w-5 h-5" />
            </Link>
            <NotificationBell />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
            {/* Back + title */}
            <div className="mb-6 flex items-center gap-2">
              <button onClick={() => navigate("/supplier/dashboard")} className="p-2 rounded-full hover:bg-accent transition-colors">
                <ArrowLeft className="w-4 h-4 text-foreground" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="font-semibold text-foreground text-base truncate">{inquiry.title}</h1>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(inquiry.created_at).toLocaleDateString()}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    inquiry.status === "open" ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
                  }`}>
                    {inquiry.status === "open" ? "Otevřená" : inquiry.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Structured parameters card */}
            <div className="border border-border rounded-xl bg-card p-4 mb-4 space-y-4">
              <h2 className="text-sm font-semibold text-foreground">Parametry poptávky</h2>

              {/* Location */}
              {inquiry.region && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                  <div>
                    <span className="text-xs text-muted-foreground">Místo stavby</span>
                    <p className="text-sm font-medium text-foreground">{inquiry.region}</p>
                  </div>
                </div>
              )}

              {/* Chat estimate section */}
              {(() => {
                const chatArea = details["Plocha"] || details["Plocha fasády"] || null;
                const chatMaterial = details["Materiál"] || details["Material"] || null;
                const chatThickness = details["Tloušťka"] || details["Tloušťka izolace"] || null;
                const priceEstimate = details["Cenový odhad"] || details["Cena"] || null;
                const contactKeys = ["telefon", "e-mail", "phone", "email", "poznámky", "termín realizace"];
                const otherDetails = Object.entries(details).filter(
                  ([k]) => !["Plocha", "Plocha fasády", "Materiál", "Material", "Tloušťka", "Tloušťka izolace", "Cenový odhad", "Cena", ...contactKeys.map(c => c.charAt(0).toUpperCase() + c.slice(1))].includes(k) &&
                    !contactKeys.some(c => k.toLowerCase().includes(c))
                );

                // Detect conflicts between chat and wall data
                const hasWalls = wallsData.length > 0;
                const wallMaterials = [...new Set(wallsData.map(w => w.material).filter(Boolean))] as string[];
                const wallThicknesses = [...new Set(wallsData.map(w => w.thickness_cm).filter(Boolean))] as number[];
                
                const materialConflict = hasWalls && chatMaterial && wallMaterials.length > 0 &&
                  !wallMaterials.some(wm => wm.toLowerCase().includes(chatMaterial.toLowerCase().split(" ")[0]) || chatMaterial.toLowerCase().includes(wm.toLowerCase().split(" ")[0]));
                
                const thicknessConflict = hasWalls && chatThickness && wallThicknesses.length > 0 &&
                  !wallThicknesses.some(wt => chatThickness.includes(String(wt)));

                const areaConflict = hasWalls && wallsTotalArea > 0 && chatArea &&
                  Math.abs(wallsTotalArea - parseFloat(chatArea)) >= 1;

                const hasAnyConflict = materialConflict || thicknessConflict || areaConflict;

                return (
                  <>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Odhad z chatu</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {chatMaterial && (
                          <div>
                            <span className="text-xs text-muted-foreground">Materiál</span>
                            <p className={`text-sm font-medium ${materialConflict ? "text-muted-foreground line-through" : "text-foreground"}`}>
                              {chatMaterial}
                            </p>
                          </div>
                        )}
                        {chatThickness && (
                          <div>
                            <span className="text-xs text-muted-foreground">Tloušťka</span>
                            <p className={`text-sm font-medium ${thicknessConflict ? "text-muted-foreground line-through" : "text-foreground"}`}>
                              {chatThickness}
                            </p>
                          </div>
                        )}
                        {chatArea && (
                          <div>
                            <span className="text-xs text-muted-foreground">Plocha (odhad)</span>
                            <p className={`text-sm font-medium ${areaConflict ? "text-muted-foreground line-through" : "text-foreground"}`}>
                              {chatArea}
                            </p>
                          </div>
                        )}
                        {priceEstimate && (
                          <div>
                            <span className="text-xs text-muted-foreground">Cenový odhad AI</span>
                            <p className="text-sm font-medium text-foreground">{priceEstimate}</p>
                          </div>
                        )}
                        {otherDetails.map(([key, val]) => (
                          <div key={key}>
                            <span className="text-xs text-muted-foreground">{key}</span>
                            <p className="text-sm font-medium text-foreground">{val}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Conflict notice */}
                    {hasAnyConflict && (
                      <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800 dark:text-amber-300 space-y-0.5">
                          <p className="font-medium">Zákazník upřesnil údaje na kartě poptávky:</p>
                          {areaConflict && (
                            <p>Plocha: <span className="line-through">{chatArea}</span> → <span className="font-semibold">{wallsTotalArea} m²</span></p>
                          )}
                          {materialConflict && (
                            <p>Materiál: <span className="line-through">{chatMaterial}</span> → <span className="font-semibold">{wallMaterials.join(", ")}</span></p>
                          )}
                          {thicknessConflict && (
                            <p>Tloušťka: <span className="line-through">{chatThickness}</span> → <span className="font-semibold">{wallThicknesses.map(t => `${t} cm`).join(", ")}</span></p>
                          )}
                          <p className="text-amber-700/70 dark:text-amber-400/70 mt-1">Naceňte podle upřesněných údajů níže.</p>
                        </div>
                      </div>
                    )}

                    {approved && Object.entries(details)
                      .filter(([k]) => contactKeys.some(c => k.toLowerCase().includes(c)) && !["poznámky", "termín realizace"].some(c => k.toLowerCase().includes(c)))
                      .map(([key, val]) => (
                        <div key={key} className="flex justify-between text-sm px-1">
                          <span className="text-muted-foreground">{key}</span>
                          <span className="font-medium text-foreground">{val}</span>
                        </div>
                      ))
                    }
                  </>
                );
              })()}

              {serviceModule?.SupplierExtras && (
                <serviceModule.SupplierExtras inquiryId={inquiry.id} />
              )}

              {/* Generic intake_data display for non-custom-module services */}
              {!serviceModule?.SupplierExtras && intakeSchema.length > 0 && inquiry.intake_data && Object.keys(inquiry.intake_data).length > 0 && (
                <div className="border border-border rounded-xl bg-card p-4">
                  <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                    <Ruler className="w-4 h-4" />
                    Parametry poptávky
                  </h2>
                  <GenericIntakeForm
                    intakeSchema={intakeSchema}
                    values={inquiry.intake_data}
                    readOnly
                  />
                </div>
              )}
            </div>

            {/* Photos */}
            {inquiry.photos && inquiry.photos.length > 0 && (
              <div className="border border-border rounded-xl bg-card p-4 mb-4">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                  <ImageIcon className="w-4 h-4" />
                  Fotografie ({inquiry.photos.length})
                </h2>
                <div className="flex flex-wrap gap-2">
                  {(signedPhotos.length > 0 ? signedPhotos : inquiry.photos).map((photo, i) => (
                    <a key={i} href={photo} target="_blank" rel="noopener noreferrer">
                      <img src={photo} alt={`Foto ${i + 1}`} className="w-28 h-28 rounded-lg object-cover border border-border hover:opacity-80 transition-opacity" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Action tabs */}
            {approved ? (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 text-center space-y-2">
                <div className="flex items-center gap-2 justify-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <p className="text-sm font-semibold text-primary">{t("supplier.customerApproved")}</p>
                </div>
                <p className="text-xs text-muted-foreground">{t("supplier.contactVisibleNow")}</p>
              </div>
            ) : quoteSent && sentQuoteData ? (
              <div className="border border-border rounded-xl bg-card overflow-hidden">
                <div className="px-5 pt-5 pb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground text-sm">{t("supplier.quoteSentConfirm")}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t("supplier.waitingCustomerApproval")}</p>
                </div>

                {/* Price summary */}
                <div className="mx-5 mb-4 border border-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-border bg-muted/30">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Souhrn nabídky</span>
                  </div>
                  <div className="px-4 py-4 space-y-2">
                    {sentQuoteData.materialCost != null && sentQuoteData.materialCost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">🧱 Materiál</span>
                        <span className="font-medium text-foreground">{sentQuoteData.materialCost.toLocaleString("cs-CZ")} Kč</span>
                      </div>
                    )}
                    {sentQuoteData.scaffoldingCost != null && sentQuoteData.scaffoldingCost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">🏗️ Lešení</span>
                        <span className="font-medium text-foreground">{sentQuoteData.scaffoldingCost.toLocaleString("cs-CZ")} Kč</span>
                      </div>
                    )}
                    {sentQuoteData.laborCost != null && sentQuoteData.laborCost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">👷 Práce</span>
                        <span className="font-medium text-foreground">{sentQuoteData.laborCost.toLocaleString("cs-CZ")} Kč</span>
                      </div>
                    )}
                    {sentQuoteData.transportCost != null && sentQuoteData.transportCost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">🚛 Doprava</span>
                        <span className="font-medium text-foreground">{sentQuoteData.transportCost.toLocaleString("cs-CZ")} Kč</span>
                      </div>
                    )}
                    {sentQuoteData.otherCosts?.map((oc, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">📦 {oc.label}</span>
                        <span className="font-medium text-foreground">{oc.value.toLocaleString("cs-CZ")} Kč</span>
                      </div>
                    ))}

                    <div className="border-t border-border pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Náklady celkem</span>
                        <span className="font-semibold text-foreground">
                          {((sentQuoteData.materialCost || 0) + (sentQuoteData.scaffoldingCost || 0) + (sentQuoteData.laborCost || 0) + (sentQuoteData.transportCost || 0) + (sentQuoteData.otherCosts?.reduce((s, c) => s + c.value, 0) || 0)).toLocaleString("cs-CZ")} Kč
                        </span>
                      </div>
                    </div>

                    {sentQuoteData.profit != null && sentQuoteData.profit > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">💰 Zisk</span>
                        <span className="font-medium text-primary">{sentQuoteData.profit.toLocaleString("cs-CZ")} Kč</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Final price */}
                <div className="mx-5 mb-4 border border-primary/30 bg-primary/5 rounded-xl p-4">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Cena odeslaná zákazníkovi</p>
                    <p className="text-2xl font-bold text-foreground">{sentQuoteData.totalPrice.toLocaleString("cs-CZ")} Kč <span className="text-sm font-normal text-muted-foreground">bez DPH</span></p>
                    {sentQuoteData.area > 0 && (
                      <p className="text-sm text-muted-foreground">{Math.round(sentQuoteData.totalPrice / sentQuoteData.area)} Kč/m²</p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      s DPH ({sentQuoteData.vatRate} %): <span className="font-semibold text-foreground">{Math.round(sentQuoteData.totalPrice * (1 + sentQuoteData.vatRate / 100)).toLocaleString("cs-CZ")} Kč</span>
                    </p>
                  </div>
                </div>

                <div className="px-5 pb-5">
                  <p className="text-xs text-muted-foreground text-center">
                    💡 Tyto detaily vidíte pouze vy. Zákazník obdržel pouze výslednou cenu.
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-full">
                {/* Facade flow actions: material inquiry and quote flow are gated separately */}
                {isFasady ? (
                <div className="w-full grid grid-cols-2 mb-4 h-auto gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveAction("material")}
                    className={`flex items-center justify-center gap-2 text-sm py-3 rounded-xl border transition-all ${
                      activeAction === "material"
                        ? "bg-primary text-primary-foreground border-primary shadow-md"
                        : canUseMaterialInquiry
                            ? "bg-card text-muted-foreground border-border"
                            : "bg-card text-muted-foreground border-border opacity-70"
                    }`}
                  >
                    <Package className="w-4 h-4" />
                    Připravit poptávku materiálu
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveAction("quote")}
                    className={`flex items-center justify-center gap-2 text-sm py-3 rounded-xl border transition-all ${
                      activeAction === "quote"
                        ? "bg-primary text-primary-foreground border-primary shadow-md"
                        : "bg-card text-muted-foreground border-border"
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    Připravit nabídku zákazníkovi
                  </button>
                </div>
                ) : null}

                {/* Priority 1: DynamicQuoteForm from approved service_quote_config (any service) */}
                {!isFasady && quoteConfig ? (
                  <DynamicQuoteForm
                    config={quoteConfig}
                    supplierId={supplier.id}
                    inquiryId={inquiry.id}
                    inquiryRegion={inquiry.region}
                    area={parseFloat(details["Plocha"] || "0") || 0}
                    onQuoteSent={(data: DynamicQuoteSentData) => {
                      setSentQuoteData({
                        totalPrice: data.totalPrice,
                        materialCost: null,
                        scaffoldingCost: null,
                        laborCost: null,
                        transportCost: null,
                        otherCosts: data.otherCosts,
                        profit: data.profit,
                        vatRate: data.vatRate ?? 12,
                        area: data.area,
                      });
                      setQuoteSent(true);
                    }}
                  />
                ) : !isFasady ? (
                  /* Simple quote form fallback for non-facade inquiries without config */
                  <SimpleQuoteForm
                    supplierId={supplier.id}
                    inquiryId={inquiry.id}
                    inquiryRegion={inquiry.region}
                    onSubmit={(totalPrice, notes) => {
                      const area = parseFloat(details["Plocha"] || "0") || 0;
                      setSentQuoteData({
                        totalPrice,
                        materialCost: null,
                        scaffoldingCost: null,
                        laborCost: null,
                        transportCost: null,
                        otherCosts: null,
                        profit: null,
                        vatRate: 12,
                        area,
                      });
                      setQuoteSent(true);
                    }}
                  />
                ) : activeAction === "material" ? (
                  <FeatureGate
                    featureKey={FEATURE_KEYS.MATERIAL_INQUIRY}
                    supplierId={supplier.id}
                    supplierProfile={{
                      enabled_services: supplier.enabled_services,
                      enabled_plugins: supplier.enabled_plugins,
                      premium_modules: supplier.premium_modules,
                    }}
                  >
                    <Suspense fallback={<div className="py-8 text-center text-muted-foreground text-sm">Načítám...</div>}>
                    <MaterialInquiryBuilder
                      supplierId={supplier.id}
                      supplierCompanyName={supplier.company_name}
                      supplierEmail={supplier.contact_email}
                      inquiryId={inquiry.id}
                      context={{
                        title: inquiry.title,
                        region: inquiry.region,
                        wallsData: wallsData,
                        totalArea: wallsTotalArea > 0 ? wallsTotalArea : (parseFloat(details["Plocha"] || details["Plocha fasády"] || "0") || 0),
                        chatMaterial: details["Materiál"] || details["Material"] || null,
                        chatThickness: details["Tloušťka"] || details["Tloušťka izolace"] || null,
                      }}
                    />
                    <MaterialInquiryResults
                      supplierId={supplier.id}
                      inquiryId={inquiry.id}
                    />
                    </Suspense>
                  </FeatureGate>
                ) : (
                  <>
                    {/* Material cost summary from collected supplier responses */}
                    <Suspense fallback={null}>
                      <MaterialCostSummary
                        supplierId={supplier.id}
                        inquiryId={inquiry.id}
                        onSelectOffer={(price, companyName) => setSelectedMaterialOffer({ price, companyName })}
                      />
                    </Suspense>
                    {/* Podvolba: kalkulace (premium) vs. generativní (všichni) */}
                    {canUse(FEATURE_KEYS.ADVANCED_PRICING) ? (
                    <div className="flex gap-2 mb-4">
                      <button
                        type="button"
                        onClick={() => { setQuoteMode("manual"); setGeneratedQuote(null); }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                          quoteMode === "manual"
                            ? "bg-primary/10 text-primary border-primary/30"
                            : "bg-card text-muted-foreground border-border hover:bg-accent"
                        }`}
                      >
                        Kalkulace
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuoteMode("generative")}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                          quoteMode === "generative"
                            ? "bg-primary/10 text-primary border-primary/30"
                            : "bg-card text-muted-foreground border-border hover:bg-accent"
                        }`}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Napiš/diktuj nabídku
                      </button>
                    </div>
                    ) : null}

                    {canUse(FEATURE_KEYS.ADVANCED_PRICING) && quoteMode === "manual" ? (
                      <Suspense fallback={<div className="py-8 text-center text-muted-foreground text-sm">Načítám...</div>}>
                      <SupplierQuoteBuilder
                        supplierId={supplier.id}
                        inquiryId={inquiry.id}
                        inquiryParams={{
                          area_m2: parseFloat(details["Plocha"] || details["Plocha fasády"] || "0") || null,
                          material: details["Materiál"] || details["Material"] || null,
                          thickness_cm: parseInt(details["Tloušťka"] || details["Tloušťka izolace"] || "0") || null,
                        }}
                        wallsTotalArea={wallsTotalArea}
                        supplierEmail={supplier.contact_email}
                        materialCostFromOffer={selectedMaterialOffer}
                        onSubmit={async (totalPrice, notes, costBreakdown) => {
                          setSavingQuote(true);
                          const { error } = await supabase.from("supplier_quotes").insert({
                            supplier_id: supplier.id,
                            inquiry_id: inquiry.id,
                            total_price: totalPrice,
                            region: inquiry.region || null,
                            notes: notes || null,
                            material_cost: costBreakdown.material_cost,
                            scaffolding_cost: costBreakdown.scaffolding_cost,
                            labor_cost: costBreakdown.labor_cost,
                            transport_cost: costBreakdown.transport_cost,
                            other_costs: costBreakdown.other_costs,
                            profit: costBreakdown.profit,
                            vat_rate: costBreakdown.vat_rate,
                          } as any);
                          if (error) {
                            setSavingQuote(false);
                            if ((error as { code?: string }).code === "23505") {
                              toast.info(t("supplier.quoteAlreadySent"));
                            } else {
                              toast.error(error.message);
                            }
                            return;
                          }
                          try {
                            const { data: { session } } = await supabase.auth.getSession();
                            if (session) {
                              await fetch(
                                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-customer-quote`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${session.access_token}`,
                                  },
                                  body: JSON.stringify({
                                    inquiry_id: inquiry.id,
                                    total_price: totalPrice,
                                  }),
                                }
                              );
                            }
                          } catch (notifyErr) {
                            console.error("Failed to notify customer:", notifyErr);
                          }
                          setSavingQuote(false);
                          const area = wallsTotalArea > 0 ? wallsTotalArea : (parseFloat(details["Plocha"] || details["Plocha fasády"] || "0") || 0);
                          setSentQuoteData({
                            totalPrice,
                            materialCost: costBreakdown.material_cost,
                            scaffoldingCost: costBreakdown.scaffolding_cost,
                            laborCost: costBreakdown.labor_cost,
                            transportCost: costBreakdown.transport_cost,
                            otherCosts: costBreakdown.other_costs,
                            profit: costBreakdown.profit,
                            vatRate: costBreakdown.vat_rate,
                            area,
                          });
                          toast.success(t("supplier.quoteSentSuccess"));
                          setQuoteSent(true);
                        }}
                        saving={savingQuote}
                      />
                      </Suspense>
                    ) : generatedQuote ? (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setGeneratedQuote(null)}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          ← Zpět na úpravu textu
                        </button>
                        <DynamicQuoteCard
                          data={generatedQuote}
                          submitting={savingQuote}
                          onSubmit={async (data) => {
                            setSavingQuote(true);
                            const insert = mapQuoteToSupplierInsert(data, {
                              supplierId: supplier.id,
                              inquiryId: inquiry.id,
                              region: inquiry.region,
                            });
                            const { error } = await supabase.from("supplier_quotes").insert(insert);
                            if (error) {
                              setSavingQuote(false);
                              if ((error as { code?: string }).code === "23505") {
                                toast.info(t("supplier.quoteAlreadySent"));
                              } else {
                                toast.error(error.message);
                              }
                              return;
                            }
                            try {
                              const { data: { session } } = await supabase.auth.getSession();
                              if (session) {
                                await fetch(
                                  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-customer-quote`,
                                  {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                      Authorization: `Bearer ${session.access_token}`,
                                    },
                                    body: JSON.stringify({
                                      inquiry_id: inquiry.id,
                                      total_price: data.total_price,
                                    }),
                                  }
                                );
                              }
                            } catch (notifyErr) {
                              console.error("Failed to notify customer:", notifyErr);
                            }
                            setSavingQuote(false);
                            const area = wallsTotalArea > 0 ? wallsTotalArea : (parseFloat(details["Plocha"] || details["Plocha fasády"] || "0") || 0);
                            setSentQuoteData({
                              totalPrice: data.total_price!,
                              materialCost: null,
                              scaffoldingCost: null,
                              laborCost: null,
                              transportCost: null,
                              otherCosts: null,
                              profit: null,
                              vatRate: 12,
                              area,
                            });
                            toast.success(t("supplier.quoteSentSuccess"));
                            setQuoteSent(true);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="border border-border rounded-xl bg-card overflow-hidden">
                        <div className="px-4 py-3 border-b border-border bg-muted/30">
                          <h3 className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                            <Sparkles className="w-4 h-4 text-primary" />
                            Napiš nebo nadiktuj nabídku
                          </h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            Např. „Sto dvacet tisíc, EPS šedý osmnáct centimetrů, lešení v ceně, od května“
                          </p>
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="relative">
                            <textarea
                              ref={textareaRef}
                              value={generativeInput}
                              onChange={(e) => {
                                setGenerativeInput(e.target.value);
                                setGenerateError(null);
                              }}
                              placeholder="Zadejte nebo nadiktujte text nabídky..."
                              rows={4}
                              className="w-full px-3 py-2.5 pr-12 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring resize-none"
                            />
                            {isSupported && (
                              <button
                                type="button"
                                onClick={() => (isListening ? stopSpeech() : startSpeech())}
                                className={`absolute right-2 top-2 p-2 rounded-lg transition-colors ${
                                  isListening
                                    ? "bg-destructive/20 text-destructive"
                                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                                }`}
                                title={isListening ? "Zastavit nahrávání" : "Zapnout diktování"}
                              >
                                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                              </button>
                            )}
                          </div>
                          {speechError && (
                            <p className="text-xs text-destructive">{speechError}</p>
                          )}
                          {generateError && (
                            <div className="flex flex-col gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                              <p className="text-sm text-destructive">{generateError}</p>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setGenerateError(null);
                                    textareaRef.current?.focus();
                                  }}
                                  className="flex-1 py-2 rounded-lg text-sm font-medium border border-border bg-background hover:bg-accent"
                                >
                                  Uprav text
                                </button>
                                <button
                                  type="button"
                                  onClick={handleGenerateQuote}
                                  className="flex-1 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:opacity-90"
                                >
                                  Zkus znovu
                                </button>
                              </div>
                            </div>
                          )}
                          {generatingQuote && (
                            <div className="space-y-3 p-4 rounded-lg bg-muted/30 animate-pulse">
                              <div className="h-4 bg-muted rounded w-3/4" />
                              <div className="h-10 bg-muted rounded" />
                              <div className="h-10 bg-muted rounded" />
                              <div className="h-10 bg-muted rounded w-1/2" />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={handleGenerateQuote}
                            disabled={!generativeInput.trim() || generatingQuote}
                            className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                          >
                            {generatingQuote ? "Zpracovávám..." : (
                              <>
                                <Sparkles className="w-4 h-4" />
                                Převést na nabídku
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
